// Global watcher for customer "Call Staff" bell presses. Mounted once in the
// app shell so it fires on EVERY staff screen. Polls a lightweight endpoint;
// when a new call arrives it plays a bell chime and forces a popup showing
// the table number + the customer's optional message. Staff Acknowledge to
// clear it.
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../App';
import { playChime } from '../utils/chimes';
import './StaffCallWatcher.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const POLL_MS = 2500;

export default function StaffCallWatcher() {
  const { token } = useAuth();
  const [queue, setQueue] = useState([]);
  const [busy, setBusy] = useState(false);
  const seenRef = useRef(new Set());

  const poll = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/calls`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return;
      const calls = await res.json();
      const stillPending = new Set(calls.map(c => c.id));
      const fresh = calls.filter(c => !seenRef.current.has(c.id));
      fresh.forEach(c => seenRef.current.add(c.id));

      setQueue(prev => {
        const kept = prev.filter(c => stillPending.has(c.id));
        const merged = [...kept];
        for (const f of fresh) if (!merged.some(c => c.id === f.id)) merged.push(f);
        return merged;
      });

      if (fresh.length > 0) {
        playChime('staff-call');
        if ('Notification' in window && Notification.permission === 'granted') {
          const c = fresh[0];
          try {
            new Notification('Customer calling', {
              body: `${c.table_number ? `Table ${c.table_number}` : 'A table'} needs assistance${c.message ? ` — ${c.message}` : ''}`,
              tag: 'staff-call', renotify: true,
            });
          } catch (_) {}
        }
      }
    } catch (_) { /* transient */ }
  }, [token]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    let timer = null;
    const tick = async () => {
      if (cancelled) return;
      if (document.visibilityState === 'visible') { try { await poll(); } catch (_) {} }
      if (!cancelled) timer = setTimeout(tick, POLL_MS);
    };
    const onVis = () => {
      if (document.visibilityState === 'visible' && !cancelled) {
        if (timer) clearTimeout(timer);
        timer = setTimeout(tick, 400);
      }
    };
    document.addEventListener('visibilitychange', onVis);
    timer = setTimeout(tick, 1500);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [poll, token]);

  const current = queue[0];
  if (!current) return null;

  async function acknowledge() {
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/calls/${current.id}/ack`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Could not acknowledge');
      setQueue(prev => prev.filter(c => c.id !== current.id));
      poll();
    } catch (e) { alert(e.message); }
    setBusy(false);
  }

  const waiting = queue.length;
  const elapsed = (() => {
    if (!current.created_at) return null;
    const t = new Date(current.created_at.includes('T') ? current.created_at : current.created_at.replace(' ', 'T') + 'Z');
    const mins = Math.max(0, Math.round((Date.now() - t.getTime()) / 60000));
    return mins <= 0 ? 'just now' : `${mins} min ago`;
  })();

  return (
    <div className="cw-overlay" role="dialog" aria-modal="true">
      <div className="cw-card">
        <div className="cw-bell" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
        </div>
        <div className="cw-eye">Customer calling</div>
        <div className="cw-title">
          {current.table_number ? <>Table <span className="cw-tnum">{current.table_number}</span></> : 'A table'} needs assistance
        </div>
        {current.message
          ? <div className="cw-msg">“{current.message}”</div>
          : <div className="cw-msg cw-msg--empty">No message — go check what they need.</div>}
        <div className="cw-meta">
          {elapsed ? `Called ${elapsed}` : ''}{waiting > 1 ? ` · ${waiting - 1} more waiting` : ''}
        </div>
        <button type="button" className="cw-ack" disabled={busy} onClick={acknowledge}>
          {busy ? 'Working…' : 'Acknowledge — on my way'}
        </button>
      </div>
    </div>
  );
}
